import React, { useState } from 'react';

interface CameraControllerProps {
  cameraUrn: string;
  onClose?: () => void;
}

const CameraController: React.FC<CameraControllerProps> = ({ cameraUrn, onClose }) => {
  const [isPanning, setIsPanning] = useState(false);

  const handlePTZCommand = async (command: string, x?: number, y?: number, zoom?: number) => {
    setIsPanning(true);
    try {
      // TODO: Implement PTZ command via WebSocket to server
      console.log('PTZ Command:', { command, x, y, zoom, cameraUrn });
      await new Promise(resolve => setTimeout(resolve, 100)); // Simulate command
    } catch (error) {
      console.error('PTZ command failed:', error);
    } finally {
      setIsPanning(false);
    }
  };

  const buttonStyle = (active: boolean = false): React.CSSProperties => ({
    background: active ? 'rgba(59, 130, 246, 0.5)' : 'rgba(30, 41, 59, 0.8)',
    border: '1px solid rgba(59, 130, 246, 0.5)',
    color: 'white',
    padding: '0.75rem',
    borderRadius: '0.375rem',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.2s',
    fontSize: '1rem',
    fontWeight: '500',
    backdropFilter: 'blur(8px)',
    userSelect: 'none',
  });

  return (
    <div style={{
      position: 'absolute',
      top: '1rem',
      right: '1rem',
      background: 'rgba(17, 24, 39, 0.95)',
      border: '1px solid rgba(75, 85, 99, 0.5)',
      borderRadius: '0.5rem',
      padding: '1rem',
      backdropFilter: 'blur(16px)',
      boxShadow: '0 10px 25px rgba(0, 0, 0, 0.5)',
      zIndex: 1000,
    }}>
      {/* Header */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        marginBottom: '1rem',
      }}>
        <h3 style={{ 
          color: 'white', 
          fontSize: '0.875rem', 
          fontWeight: '600',
          margin: 0,
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
        }}>
          <span>🎯</span>
          PTZ Control
        </h3>
        {onClose && (
          <button
            onClick={onClose}
            style={{
              background: 'transparent',
              border: 'none',
              color: '#9ca3af',
              cursor: 'pointer',
              fontSize: '1.25rem',
              padding: '0.25rem',
              lineHeight: 1,
            }}
          >
            ✕
          </button>
        )}
      </div>

      {/* Directional Pad */}
      <div style={{ marginBottom: '1rem' }}>
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: '0.5rem',
          width: '150px',
        }}>
          {/* Top Row */}
          <div />
          <button
            onMouseDown={() => handlePTZCommand('up', 0, 0.5)}
            onMouseUp={() => handlePTZCommand('stop')}
            onMouseLeave={() => handlePTZCommand('stop')}
            style={buttonStyle(isPanning)}
          >
            ▲
          </button>
          <div />

          {/* Middle Row */}
          <button
            onMouseDown={() => handlePTZCommand('left', -0.5, 0)}
            onMouseUp={() => handlePTZCommand('stop')}
            onMouseLeave={() => handlePTZCommand('stop')}
            style={buttonStyle(isPanning)}
          >
            ◄
          </button>
          <button
            onClick={() => handlePTZCommand('home')}
            style={{
              ...buttonStyle(),
              background: 'rgba(16, 185, 129, 0.3)',
              borderColor: 'rgba(16, 185, 129, 0.5)',
              fontSize: '0.75rem',
            }}
          >
            HOME
          </button>
          <button
            onMouseDown={() => handlePTZCommand('right', 0.5, 0)}
            onMouseUp={() => handlePTZCommand('stop')}
            onMouseLeave={() => handlePTZCommand('stop')}
            style={buttonStyle(isPanning)}
          >
            ►
          </button>

          {/* Bottom Row */}
          <div />
          <button
            onMouseDown={() => handlePTZCommand('down', 0, -0.5)}
            onMouseUp={() => handlePTZCommand('stop')}
            onMouseLeave={() => handlePTZCommand('stop')}
            style={buttonStyle(isPanning)}
          >
            ▼
          </button>
          <div />
        </div>
      </div>

      {/* Zoom Controls */}
      <div style={{ 
        display: 'flex', 
        flexDirection: 'column',
        gap: '0.5rem',
      }}>
        <button
          onMouseDown={() => handlePTZCommand('zoomIn', 0, 0, 0.5)}
          onMouseUp={() => handlePTZCommand('stop')}
          onMouseLeave={() => handlePTZCommand('stop')}
          style={{
            ...buttonStyle(isPanning),
            fontSize: '0.875rem',
          }}
        >
          🔍+ Zoom In
        </button>
        <button
          onMouseDown={() => handlePTZCommand('zoomOut', 0, 0, -0.5)}
          onMouseUp={() => handlePTZCommand('stop')}
          onMouseLeave={() => handlePTZCommand('stop')}
          style={{
            ...buttonStyle(isPanning),
            fontSize: '0.875rem',
          }}
        >
          🔍- Zoom Out
        </button>
      </div>

      {/* Preset Positions (Optional) */}
      <div style={{ 
        marginTop: '1rem',
        paddingTop: '1rem',
        borderTop: '1px solid rgba(75, 85, 99, 0.3)',
      }}>
        <div style={{ 
          color: '#9ca3af', 
          fontSize: '0.75rem', 
          marginBottom: '0.5rem',
          fontWeight: '500',
        }}>
          Quick Presets
        </div>
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(2, 1fr)',
          gap: '0.5rem',
        }}>
          {[1, 2, 3, 4].map(preset => (
            <button
              key={preset}
              onClick={() => handlePTZCommand('preset', preset)}
              style={{
                ...buttonStyle(),
                fontSize: '0.75rem',
                padding: '0.5rem',
              }}
            >
              Preset {preset}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default CameraController;

